import React from 'react';
import {Link} from '@pepsico-ds/ui';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';

import {FONT_SIZE, FONT_WEIGHT} from '@/utils/constants/globalStyle';
import {COLOR_CODE} from '@/utils/constants/colors';
import './_linkText.scss';
/**
 * LinkText component renders a styled link with customizable text, font size, font weight, and color.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.id - The unique identifier for the link.
 * @param {string} props.text - The text to be displayed inside the link.
 * @param {('small'|'medium'|'large')} [props.fontSize=FONT_SIZE.SM] - The font size of the link text.
 * @param {string} [props.fontWeight=FONT_WEIGHT.NORMAL] - The font weight of the link text.
 * @param {string} [props.fontColor=COLOR.PRIMARY] - The color of the link text.
 * @param {function} props.handleClick - The function to handle click events on the link.
 * @param {string} [props.id=''] - Optional additional id to apply to the link text.
 * @param {string} [props.className=''] - Optional className prop to add classes.
 * @returns {JSX.Element} The rendered LinkText component.
 */
const LinkText = ({
  id,
  text,
  fontSize = FONT_SIZE.SM,
  fontWeight = FONT_WEIGHT.NORMAL,
  fontColor = COLOR_CODE._3387DF,
  handleClick,
  isPaddingHidden = false,
  className = '',
  ...rest
}) => {
  return (
    <Box>
      <Box
        className={` ${isPaddingHidden ? 'p-0' : 'px-2 py-1'}${className}`}
        {...rest}>
        <Link
          className={'lt-link-block'}
          style={{
            '--dynamic-color': fontColor,
            '--dynamic-font-size': fontSize,
            '--dynamic-font-weight': fontWeight
          }}
          label={text}
          id={`link_text_${id}`}
          name={`link_text_${id}`}
          onClick={handleClick}
        />
      </Box>
    </Box>
  );
};

LinkText.propTypes = {
  id: PropTypes.string,
  text: PropTypes.string.isRequired,
  fontSize: PropTypes.oneOf(['small', 'medium', 'large']),
  fontWeight: PropTypes.string,
  fontColor: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  isPaddingHidden: PropTypes.bool,
  className: PropTypes.string
};

export default React.memo(LinkText);
